import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAuth } from '../../../contexts/AuthContext';
import dynamicOptionsService from '../../../utils/dynamicOptionsService';
import organizationSeedingService from '../../../utils/organizationSeedingService';
import { isDefaultLeadSource, DEFAULT_LEAD_SOURCES } from '../../../utils/defaultLeadSources';

const DynamicOptionsManager = () => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const [leadSources, setLeadSources] = useState([]);
  const [primaryServices, setPrimaryServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sources');
  const [editingItem, setEditingItem] = useState(null);
  const [newItemName, setNewItemName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  // Load options when organization is available
  useEffect(() => {
    if (currentOrganization?.id) {
      loadOptions();
    }
  }, [currentOrganization?.id]);

  const loadOptions = async () => {
    if (!currentOrganization?.id) {
      console.warn('Cannot load options: no organization ID available');
      return;
    }

    try {
      setLoading(true);
      const [sourcesData, servicesData] = await Promise.all([
        dynamicOptionsService.getLeadSources(currentOrganization.id),
        dynamicOptionsService.getPrimaryServices(currentOrganization.id)
      ]);
      setLeadSources(sourcesData);
      setPrimaryServices(servicesData);
    } catch (error) {
      console.error('Error loading options:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add new lead source
  const handleAddLeadSource = async () => {
    if (!newItemName.trim() || !currentOrganization?.id) return;

    try {
      setIsAdding(true);
      const newSource = await dynamicOptionsService.createLeadSource(
        newItemName.trim(),
        currentOrganization.id
      );
      setLeadSources(prev => [...prev, newSource]);
      setNewItemName('');
    } catch (error) {
      console.error('Error adding lead source:', error);
    } finally {
      setIsAdding(false);
    }
  };

  // Add new primary service
  const handleAddPrimaryService = async () => {
    if (!newItemName.trim() || !currentOrganization?.id) return;

    try {
      setIsAdding(true);
      const newService = await dynamicOptionsService.createPrimaryService(
        newItemName.trim(),
        currentOrganization.id
      );
      setPrimaryServices(prev => [...prev, newService]);
      setNewItemName('');
    } catch (error) {
      console.error('Error adding primary service:', error);
    } finally {
      setIsAdding(false);
    }
  };

  // Update lead source
  const handleUpdateLeadSource = async (id, name) => {
    try {
      const updatedSource = await dynamicOptionsService.updateLeadSource(id, name);
      setLeadSources(prev => prev.map(item => item.id === id ? updatedSource : item));
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating lead source:', error);
    }
  };

  // Update primary service
  const handleUpdatePrimaryService = async (id, name) => {
    try {
      const updatedService = await dynamicOptionsService.updatePrimaryService(id, name);
      setPrimaryServices(prev => prev.map(item => item.id === id ? updatedService : item));
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating primary service:', error);
    }
  };

  // Delete lead source and handle customers using it
  const handleDeleteLeadSource = async (id) => {
    if (!confirm('Are you sure you want to delete this lead source? All customers using this source will have their lead source set to blank.')) {
      return;
    }

    try {
      // First, update all contacts using this lead source to null
      await dynamicOptionsService.clearLeadSourceFromContacts(id);
      
      // Then delete the lead source
      await dynamicOptionsService.deleteLeadSource(id);
      setLeadSources(prev => prev.filter(item => item.id !== id));
    } catch (error) {
      console.error('Error deleting lead source:', error);
    }
  };

  // Delete primary service and handle customers using it
  const handleDeletePrimaryService = async (id) => {
    if (!confirm('Are you sure you want to delete this primary service? All customers using this service will have their primary service set to blank.')) {
      return;
    }

    try {
      // First, update all contacts using this primary service to null
      await dynamicOptionsService.clearPrimaryServiceFromContacts(id);
      
      // Then delete the primary service
      await dynamicOptionsService.deletePrimaryService(id);
      setPrimaryServices(prev => prev.filter(item => item.id !== id));
    } catch (error) {
      console.error('Error deleting primary service:', error);
    }
  };

  // Restore missing default lead sources
  const handleRestoreDefaults = async () => {
    if (!currentOrganization?.id || !user?.id) return;

    try {
      setIsRestoring(true);
      const addedSources = await organizationSeedingService.addMissingDefaultLeadSources(
        currentOrganization.id,
        user.id
      );
      
      if (addedSources.length > 0) {
        // Refresh the lead sources list
        await loadOptions();
        alert(`Successfully restored ${addedSources.length} default lead sources!`);
      } else {
        alert('All default lead sources are already available.');
      }
    } catch (error) {
      console.error('Error restoring defaults:', error);
      alert('Failed to restore default lead sources. Please try again.');
    } finally {
      setIsRestoring(false);
    }
  };

  // Get missing default lead sources
  const getMissingDefaults = () => {
    if (!leadSources.length) return [];
    
    const existingNames = new Set(leadSources.map(source => source.name.toLowerCase()));
    return DEFAULT_LEAD_SOURCES.filter(defaultSource => 
      !existingNames.has(defaultSource.name.toLowerCase())
    );
  };

  // Categorize lead sources
  const categorizeLeadSources = () => {
    const defaults = [];
    const custom = [];
    
    leadSources.forEach(source => {
      if (isDefaultLeadSource(source.name)) {
        defaults.push(source);
      } else {
        custom.push(source);
      }
    });
    
    return { defaults, custom };
  };

  const renderOptionsList = (items, onUpdate, onDelete, showDefaultIndicator = false) => (
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item.id} className="bg-white border border-border rounded-lg p-4">
          {editingItem === item.id ? (
            <EditForm
              item={item}
              onSave={(name) => onUpdate(item.id, name)}
              onCancel={() => setEditingItem(null)}
            />
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-text-primary">{item.name}</h4>
                  {showDefaultIndicator && isDefaultLeadSource(item.name) && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <Icon name="Star" size={12} className="mr-1" />
                      Default
                    </span>
                  )}
                </div>
                <p className="text-xs text-text-secondary mt-1">
                  Created: {new Date(item.created_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingItem(item.id)}
                  className="p-2 hover:bg-gray-100"
                  title="Edit"
                >
                  <Icon name="Edit" size={16} className="text-gray-600" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(item.id)}
                  className="p-2 hover:bg-red-50"
                  title="Delete"
                >
                  <Icon name="Trash2" size={16} className="text-red-600" />
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const EditForm = ({ item, onSave, onCancel }) => {
    const [name, setName] = useState(item.name);
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
      if (!name.trim()) return;

      setSaving(true);
      try {
        await onSave(name.trim());
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="space-y-3">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          className="w-full"
        />
        <div className="flex items-center space-x-2">
          <Button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            size="sm"
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            variant="ghost"
            onClick={onCancel}
            size="sm"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center justify-center py-8">
          <Icon name="Loader" size={24} className="animate-spin text-text-secondary" />
          <span className="ml-2 text-text-secondary">Loading options...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          Dynamic Customer Options
        </h3>
        <p className="text-sm text-text-secondary">
          Manage lead sources and primary services that users can select or add when creating customers.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-6">
        <button
          onClick={() => setActiveTab('sources')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'sources'
              ? 'bg-primary text-white'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          Lead Sources ({leadSources.length})
        </button>
        <button
          onClick={() => setActiveTab('services')}
          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
            activeTab === 'services'
              ? 'bg-primary text-white'
              : 'text-text-secondary hover:text-text-primary'
          }`}
        >
          Primary Services ({primaryServices.length})
        </button>
      </div>

      {/* Add New Form */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-text-primary mb-3">
          Add New {activeTab === 'sources' ? 'Lead Source' : 'Primary Service'}
        </h4>
        <div className="flex gap-3">
          <Input
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            placeholder="Name"
            className="flex-1"
          />
          <Button
            onClick={activeTab === 'sources' ? handleAddLeadSource : handleAddPrimaryService}
            disabled={isAdding || !newItemName.trim()}
          >
            {isAdding ? 'Adding...' : `Add ${activeTab === 'sources' ? 'Source' : 'Service'}`}
          </Button>
        </div>
      </div>

      {/* Options List */}
      <div>
        {activeTab === 'sources' ? (
          <>
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-text-primary">Lead Sources</h4>
              {getMissingDefaults().length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRestoreDefaults}
                  disabled={isRestoring}
                  className="flex items-center gap-2"
                >
                  <Icon name="RotateCcw" size={14} />
                  {isRestoring ? 'Restoring...' : `Restore ${getMissingDefaults().length} Defaults`}
                </Button>
              )}
            </div>
            
            {leadSources.length === 0 ? (
              <p className="text-text-secondary text-center py-8">
                No lead sources found. Add one above to get started.
              </p>
            ) : (
              <>
                {(() => {
                  const { defaults, custom } = categorizeLeadSources();
                  return (
                    <div className="space-y-6">
                      {defaults.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-text-secondary mb-3 flex items-center gap-2">
                            <Icon name="Star" size={14} />
                            Default Sources ({defaults.length})
                          </h5>
                          {renderOptionsList(defaults, handleUpdateLeadSource, handleDeleteLeadSource, true)}
                        </div>
                      )}
                      
                      {custom.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-text-secondary mb-3 flex items-center gap-2">
                            <Icon name="Plus" size={14} />
                            Custom Sources ({custom.length})
                          </h5>
                          {renderOptionsList(custom, handleUpdateLeadSource, handleDeleteLeadSource, false)}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </>
        ) : (
          <>
            <h4 className="font-medium text-text-primary mb-4">Primary Services</h4>
            {primaryServices.length === 0 ? (
              <p className="text-text-secondary text-center py-8">
                No primary services found. Add one above to get started.
              </p>
            ) : (
              renderOptionsList(primaryServices, handleUpdatePrimaryService, handleDeletePrimaryService)
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DynamicOptionsManager; 